/**
 * ============================================================
 * KEJJA OS — src/pages/meals/MealsLayout.tsx
 * Amber accent. 4-tab sub-shell.
 * ============================================================
 */

import { NavLink, Outlet, useOutletContext } from 'react-router-dom';
import { ChefHat, Sun, CalendarDays, ShoppingCart, BookOpen } from 'lucide-react';
import { useMyHomes } from '@/hooks/useMe';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface MealsContext {
  homeId: string;
  currentUserId: string | undefined;
  homeName: string;
  currencyCode: string;
}

export function useMealsContext(): MealsContext {
  return useOutletContext<MealsContext>();
}

const TABS = [
  { to: '/meals',          label: 'Today',    icon: Sun,          end: true },
  { to: '/meals/plan',     label: 'Plan',     icon: CalendarDays },
  { to: '/meals/shopping', label: 'Shopping', icon: ShoppingCart },
  { to: '/meals/recipes',  label: 'Recipes',  icon: BookOpen },
];

export default function MealsLayout() {
  const { data, isLoading } = useMyHomes();
  const first = data?.homes?.[0];
  const homeId = first?.home.id;
  const homeName = first?.home.name ?? '';
  const currencyCode = first?.home.currencyCode ?? 'KES';
  const currentUserId = undefined;   // TODO: pull from /me when needed

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!homeId) {
    return <div className="text-sm text-muted-foreground">No home found. Create a home first.</div>;
  }

  return (
    <div className="space-y-6">
      <header className="border-b border-border pb-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 flex items-center justify-center flex-shrink-0">
            <ChefHat className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-foreground leading-tight tracking-tight">
              Meals
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              <span className="italic">What's cooking, who's shopping.</span>
              {homeName && <span className="ml-2">· {homeName}</span>}
            </p>
          </div>
        </div>
      </header>

      <nav className="flex items-center gap-1 overflow-x-auto">
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) => cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors',
              isActive
                ? 'bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
          </NavLink>
        ))}
      </nav>

      <Outlet context={{ homeId, currentUserId, homeName, currencyCode } satisfies MealsContext} />
    </div>
  );
}
